import { v4 as uuidv4 } from "uuid";
import { useMutation } from "@tanstack/react-query";

import { submitPackagingSolutions } from "@/api/import-api";
import { useMachineGroups } from "@/hooks/machine-groups";
import { type MachineGroup } from "@/schemas";

type Location = {
	packStation: string | null;
	machine: string | null;
	machineGroup: string;
	productionGroup: string | null;
	pickZone: string | null;
};

type SolutionToSend = Record<string, unknown> & {
	location?: Location;
	quantity?: number;
	batchId?: string;
	attributes?: Record<string, unknown>;
};

type SubmitTestJobParams = {
	solution: SolutionToSend;
	machineId: string;
};

const findMachineGroupForMachine = (machineGroups: MachineGroup[], machineId: string): MachineGroup | undefined => {
	return machineGroups.find((mg) => {
		const machines = Object.keys(mg.configuredMachinesWithMachineType);
		return machines.includes(machineId);
	});
};

export const useSubmitTestJob = () => {
	const { machineGroups } = useMachineGroups();

	const mutation = useMutation({
		mutationFn: async ({ solution, machineId }: SubmitTestJobParams) => {
			if (!machineGroups) {
				throw new Error("Machine groups not available");
			}

			const machineGroup = findMachineGroupForMachine(machineGroups, machineId);

			if (!machineGroup) {
				throw new Error("Machine group not found for the selected machine");
			}

			const location: Location = {
				packStation: null,
				machine: null,
				machineGroup: machineGroup.alias,
				productionGroup: null,
				pickZone: null,
			};

			const solutionWithLocation = {
				...solution,
				location,
				quantity: 1,
				batchId: uuidv4(),
				attributes: { source: "PackageCreation" },
			};

			return submitPackagingSolutions([solutionWithLocation]);
		},
	});

	return {
		submitJob: mutation.mutateAsync,
		isPending: mutation.isPending,
		isSuccess: mutation.isSuccess,
		isError: mutation.isError,
		error: mutation.error,
	};
};
